// Updated File: src/components/HeroSection.jsx
// Changes: Fixed banner cropping issue for responsive design
// Banners now use object-fit: contain with background for better display

import React, { useState, useEffect, useRef } from 'react';
import { themeAPI } from '../services/api';
import './css/HeroSection.css';

const BannerCarousel = ({ images, bannerId }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [prevTranslate, setPrevTranslate] = useState(0);
  const timeoutRef = useRef(null);
  const sliderRef = useRef(null);
  const dragRafRef = useRef(null);
  const pendingTranslateRef = useRef(0);

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  useEffect(() => {
    resetTimeout();
    if (isAutoPlaying && images.length > 1 && !isDragging) {
      timeoutRef.current = setTimeout(() => {
        goToNext();
      }, 5000);
    }

    return () => {
      resetTimeout();
    };
  }, [currentIndex, isAutoPlaying, images.length, isDragging]);

  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const goToPrev = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
    setIsAutoPlaying(false);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const handleNext = () => {
    goToNext();
    setIsAutoPlaying(false);
  };

  // Touch and Mouse drag handlers
  const getPositionX = (event) => {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  };

  const handleDragStart = (event) => {
    if (images.length <= 1) return;
    setIsDragging(true);
    setIsAutoPlaying(false);
    setStartPos(getPositionX(event));
    if (sliderRef.current) {
      sliderRef.current.classList.add('dragging');
    }
  };

  const handleDragMove = (event) => {
    if (!isDragging) return;
    const currentPosition = getPositionX(event);
    const diff = currentPosition - startPos;
    pendingTranslateRef.current = prevTranslate + diff;
    if (dragRafRef.current) return;
    dragRafRef.current = requestAnimationFrame(() => {
      setCurrentTranslate(pendingTranslateRef.current);
      dragRafRef.current = null;
    });
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    const movedBy = currentTranslate - prevTranslate;
    const threshold = sliderRef.current ? sliderRef.current.offsetWidth * 0.2 : 100;
    if (dragRafRef.current) {
      cancelAnimationFrame(dragRafRef.current);
      dragRafRef.current = null;
    }

    if (sliderRef.current) {
      sliderRef.current.classList.remove('dragging');
    }

    if (movedBy < -threshold && currentIndex < images.length - 1) {
      goToNext();
    } else if (movedBy > threshold && currentIndex > 0) {
      goToPrev();
    }

    setCurrentTranslate(0);
    setPrevTranslate(0);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      handleDragEnd();
    }
  };

  return (
    <div 
      className="hero-banner-slider"
      ref={sliderRef}
      onMouseDown={handleDragStart}
      onMouseMove={handleDragMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleDragStart}
      onTouchMove={handleDragMove}
      onTouchEnd={handleDragEnd}
    >
      <div 
        className="hero-banner-track"
        style={{ 
          transform: isDragging 
            ? `translateX(calc(-${currentIndex * 100}% + ${currentTranslate}px))` 
            : `translateX(-${currentIndex * 100}%)`
        }}
      >
        {images.map((item, index) => (
          <div key={index} className="hero-banner-slide">
            <a 
              href={item.link}
              onClick={(e) => {
                if (Math.abs(currentTranslate) > 10) {
                  e.preventDefault();
                }
              }}
              onDragStart={(e) => e.preventDefault()}
            >
              <img 
                src={item.image} 
                alt={item.alt || `Banniere ${bannerId} - Diapositive ${index + 1}`}
                draggable="false"
                decoding="async"
              />
            </a>
          </div>
        ))}
      </div>

      {images.length > 1 && (
        <>
          <button 
            className="hero-banner-arrow left" 
            onClick={goToPrev}
            aria-label="Diapositive precedente"
          >
            ‹
          </button>
          <button 
            className="hero-banner-arrow right" 
            onClick={handleNext}
            aria-label="Diapositive suivante"
          >
            ›
          </button>

          <div className="hero-banner-dots">
            {images.map((_, index) => (
              <button
                key={index}
                className={`hero-banner-dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Aller a la diapositive ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const HeroSection = () => {
  const [banners, setBanners] = useState([
    { id: 1, images: [] },
    { id: 2, images: [] },
    { id: 3, images: [] },
    { id: 4, images: [] },
    { id: 5, images: [] }
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Demo fallback
  const demoImage1 = "https://graphicsfamily.com/wp-content/uploads/edd/2022/12/E-commerce-Product-Banner-Design-2048x1152.jpg";
  const demoImage2 = "https://cdn.dribbble.com/users/3077944/screenshots/9520028/media/e31cd9f99df283a4575f2b2d143b9772.jpg?resize=400x300&vertical=center";
  const demoImages = [{ image: demoImage1, link: "/products" }, { image: demoImage2, link: "/products" }];

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await themeAPI.getBanners();
        if (response.data.success) {
          const data = response.data.data;
          const updatedBanners = banners.map((banner) => ({
            ...banner,
            images: data[banner.id] ? data[banner.id].map(item => ({
              image: item.image,
              link: item.link,
              alt: item.alt_text
            })) : []
          }));
          setBanners(updatedBanners);
        }
      } catch (err) {
        console.error('Failed to fetch banners:', err);
        setError('Impossible de charger les bannieres. Version demo.');
        // Fallback to demo
        setBanners(banners.map(() => ({ images: demoImages })));
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  if (loading) {
    return (
      <section className="hero-section">
        <div className="hero-container">
          <div style={{ textAlign: 'center', padding: '2rem' }}>Chargement des bannieres...</div>
        </div>
      </section>
    );
  }

  return (
    <section className="hero-section">
      <div className="hero-container">
        {/* Desktop Layout */}
        <div className="hero-desktop-layout">
          {/* Left Column */}
          <div className="hero-left-column">
            <div className="hero-banner hero-banner-1">
              <BannerCarousel images={banners[0].images} bannerId={1} />
            </div>
            <div className="hero-banner hero-banner-2">
              <BannerCarousel images={banners[1].images} bannerId={2} />
            </div>
          </div>

          {/* Center Column */}
          <div className="hero-center-column">
            <div className="hero-banner hero-banner-3 hero-banner-large">
              <BannerCarousel images={banners[2].images} bannerId={3} />
            </div>
          </div>

          {/* Right Column */}
          <div className="hero-right-column">
            <div className="hero-banner hero-banner-4">
              <BannerCarousel images={banners[3].images} bannerId={4} />
            </div>
            <div className="hero-banner hero-banner-5">
              <BannerCarousel images={banners[4].images} bannerId={5} />
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="hero-mobile-layout">
          <div className="hero-mobile-row">
            <div className="hero-banner hero-banner-mobile">
              <BannerCarousel images={banners[0].images} bannerId={1} />
            </div>
            <div className="hero-banner hero-banner-mobile">
              <BannerCarousel images={banners[1].images} bannerId={2} />
            </div>
          </div>

          <div className="hero-mobile-large">
            <div className="hero-banner hero-banner-mobile-large">
              <BannerCarousel images={banners[2].images} bannerId={3} />
            </div>
          </div>

          <div className="hero-mobile-row">
            <div className="hero-banner hero-banner-mobile">
              <BannerCarousel images={banners[3].images} bannerId={4} />
            </div>
            <div className="hero-banner hero-banner-mobile">
              <BannerCarousel images={banners[4].images} bannerId={5} />
            </div>
          </div>
        </div>
      </div>
      {error && <div style={{ textAlign: 'center', color: 'orange', padding: '1rem' }}>{error}</div>}
    </section>
  );
};

export default HeroSection;
